import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { dietAPI } from '../services/api';
import axios from 'axios';
import {
    Box, Typography, Card, CardContent, Button, Alert, CircularProgress,
    Chip, CardMedia, IconButton, useTheme, Drawer, useMediaQuery, Fab,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Stepper, Step, StepLabel, Zoom, Grid, Snackbar
} from '@mui/material';
import {
    Restaurant, Menu as MenuIcon, Close, ShoppingCart,
    FitnessCenter, EmojiEvents, Article, CheckCircle,
    CloudUpload, DeleteOutline
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import UserNavbar from '../components/UserNavbar';
import SideMenu from '../components/SideMenu';
import Footer from '../components/Footer';

const STEPS = ['Податоци', 'Прашалник', 'Потврда'];

const UserBuyDiets = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const theme = useTheme();
    const { isDarkMode } = useCustomTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

    const [diets, setDiets] = useState([]);
    const [myDiets, setMyDiets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const [viewDiet, setViewDiet] = useState(null);
    const [purchaseDialog, setPurchaseDialog] = useState({ open: false, diet: null });
    const [activeStep, setActiveStep] = useState(0);

    const [bmiData, setBmiData] = useState({ height: '', weight: '', waistCircumference: '' });
    const [bmiErrors, setBmiErrors] = useState({});
    const [questionnaire, setQuestionnaire] = useState({
        age: '',
        dietGoal: '',
        healthProblems: '',
        foodIntolerances: '',
        supplements: ''
    });
    const [ageError, setAgeError] = useState('');
    const [bloodTestFile, setBloodTestFile] = useState(null);
    const [bloodTestPreview, setBloodTestPreview] = useState('');

    useEffect(() => { fetchDiets(); }, []);

    const fetchDiets = async () => {
        try {
            setLoading(true);
            const dietsData = await dietAPI.getAll();
            setDiets(dietsData.map((diet, index) => ({ ...diet, color: getColorForIndex(index) })));
            try { setMyDiets(await dietAPI.getMyDiets()); } catch (err) {}
        } catch (err) { setError('Грешка при вчитување.'); } finally { setLoading(false); }
    };

    const getColorForIndex = (i) => {
        const colors = ['#4CAF50', '#66BB6A', '#81C784', '#8BC34A', '#9CCC65'];
        return colors[i % colors.length];
    };

    const menuItems = [
        { id: 1, title: 'Купи Програми', description: 'Прегледај и купи тренинг програми', icon: <FitnessCenter />, path: '/buy-programs', color: '#ff7eb9', available: true, badge: 'Популарно' },
        { id: 2, title: 'Купи Диета', description: 'Здравствени диететски планови', icon: <Restaurant />, path: '/user-diets', color: '#4CAF50', available: true, badge: 'Препорачано' },
        { id: 3, title: 'Блог', description: 'Читај статии за фитнес и здравје', icon: <Article />, path: '/user-blog', color: '#a5d8ff', available: true, badge: 'Ново' },
        { id: 4, title: 'Достигнувања', description: 'Следете ги вашите достигнувања', icon: <EmojiEvents />, path: '/achievements', color: '#ffd166', available: false, badge: 'Наскоро' }
    ];

    const handleItemClick = (item) => {
        setSelectedItem(item);
        if (isMobile) setMobileMenuOpen(false);
        if (!item.available) {
            alert('🚧 Оваа функционалност е во развој и наскоро ќе биде достапна!');
            return;
        }
        navigate(item.path);
    };

    const handleItemHover = (item) => {
        if (!isMobile) setSelectedItem(item);
    };

    const handleBuyClick = (diet, e) => {
        if (e) e.stopPropagation();
        setViewDiet(null);
        setPurchaseDialog({ open: true, diet });
        setActiveStep(0);
    };

    // ✅ Прикачување слика од крвна анализа (опционално)
    const handleBloodTestChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setBloodTestFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setBloodTestPreview(reader.result);
        reader.readAsDataURL(file);
    };

    const handleRemoveBloodTest = () => {
        setBloodTestFile(null);
        setBloodTestPreview('');
    };

    const handleFinalSubmit = async () => {
        const diet = purchaseDialog.diet;
        setSubmitting(true);
        try {
            // ✅ Обидот за "купување" (BMI запис) не смее да го блокира прашалникот -
            // немаме реален систем за плаќање, само ги чуваме BMI податоците ако успее.
            try {
                await dietAPI.purchase(diet.id, {
                    height: parseFloat(bmiData.height),
                    weight: parseFloat(bmiData.weight),
                    waistCircumference: parseFloat(bmiData.waistCircumference)
                });
            } catch (purchaseErr) {
                console.warn('BMI запис не успеа, продолжуваме со прашалникот:', purchaseErr);
            }

            const formDataToSend = new FormData();
            formDataToSend.append('age', questionnaire.age || '');
            formDataToSend.append('dietGoal', questionnaire.dietGoal || '');
            formDataToSend.append('healthProblems', questionnaire.healthProblems || '');
            formDataToSend.append('foodIntolerances', questionnaire.foodIntolerances || '');
            formDataToSend.append('supplements', questionnaire.supplements || '');
            if (bloodTestFile) formDataToSend.append('bloodTest', bloodTestFile);
            const token = localStorage.getItem('token');
            await axios.post(`${process.env.REACT_APP_API_URL}/diets/${diet.id}/questionnaire`, formDataToSend, {
                headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
            });
            setActiveStep(3);
            fetchDiets();
        } catch (err) {
            setSnackbar({
                open: true,
                message: '❌ Грешка при испраќање на прашалникот. Обидете се повторно.',
                severity: 'error'
            });
        } finally { setSubmitting(false); }
    };

    const userName = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : 'Корисник';

    if (loading) return (
        <>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}><UserNavbar /></Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#4CAF50' }} />
            </Box>
        </>
    );

    return (
        <>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}><UserNavbar /></Box>

            <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>

                {/* ===== DESKTOP SIDEBAR ===== */}
                {!isMobile && (
                    <Box sx={{
                        width: isTablet ? '280px' : '320px',
                        backgroundColor: 'background.paper',
                        borderRight: 1,
                        borderColor: 'divider',
                        flexShrink: 0,
                        position: 'sticky',
                        top: '64px',
                        height: 'calc(100vh - 64px)',
                        overflowY: 'auto',
                        zIndex: 999
                    }}>
                        <SideMenu
                            menuItems={menuItems}
                            selectedItem={selectedItem}
                            onItemClick={handleItemClick}
                            onItemHover={handleItemHover}
                            programsCount={0}
                            userName={userName}
                        />
                    </Box>
                )}

                {/* ===== MOBILE DRAWER ===== */}
                {isMobile && (
                    <>
                        <Drawer
                            anchor="left"
                            open={mobileMenuOpen}
                            onClose={() => setMobileMenuOpen(false)}
                            PaperProps={{
                                sx: {
                                    width: '280px',
                                    backgroundColor: 'background.paper',
                                    borderRight: 1,
                                    borderColor: 'divider'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                                <IconButton onClick={() => setMobileMenuOpen(false)}>
                                    <Close />
                                </IconButton>
                            </Box>
                            <SideMenu
                                menuItems={menuItems}
                                selectedItem={selectedItem}
                                onItemClick={handleItemClick}
                                onItemHover={handleItemHover}
                                programsCount={0}
                                userName={userName}
                            />
                        </Drawer>
                        <Fab
                            color="primary"
                            onClick={() => setMobileMenuOpen(true)}
                            sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: 1000 }}
                        >
                            <MenuIcon />
                        </Fab>
                    </>
                )}

                {/* ===== MAIN CONTENT ===== */}
                <Box sx={{ flex: 1, p: { xs: 2, sm: 3, md: 4 }, overflow: 'auto' }}>
                    <Typography
                        variant={isMobile ? 'h4' : 'h3'}
                        sx={{
                            fontWeight: 'bold', mb: 4, color: '#4CAF50',
                            fontSize: isMobile ? '1.8rem' : isTablet ? '2.2rem' : '3rem'
                        }}
                    >
                        🥗 Купи Диета
                    </Typography>

                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                    <Box sx={{
                        display: 'flex',
                        gap: 3,
                        overflowX: 'auto',
                        pb: 2,
                        flexWrap: { xs: 'nowrap', md: 'wrap' }
                    }}>
                        {diets.map(diet => (
                            <Card key={diet.id} onClick={() => setViewDiet(diet)} sx={{
                                minWidth: { xs: 240, sm: 280 },
                                maxWidth: { md: 320 },
                                borderRadius: 3,
                                border: `2px solid ${diet.color}`,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 }
                            }}>
                                <CardMedia component="div" sx={{ height: 140, bgcolor: diet.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Restaurant sx={{ fontSize: 50, color: 'white' }} />
                                </CardMedia>
                                <CardContent>
                                    <Typography variant="h6" fontWeight="bold">{diet.name}</Typography>
                                    <Typography variant="h5" sx={{ color: diet.color, mt: 2, textAlign: 'center', fontWeight: 'bold' }}>{diet.price} ден</Typography>
                                    <Button fullWidth variant="contained" sx={{ mt: 2, bgcolor: diet.color }} onClick={(e) => handleBuyClick(diet, e)}>Купи</Button>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>

                    {/* Spacer за FAB на мобилен */}
                    <Box sx={{ height: { xs: '80px', md: '40px' } }} />
                </Box>
            </Box>

            {/* FULL SCREEN PREVIEW */}
            <Dialog fullScreen open={!!viewDiet} onClose={() => setViewDiet(null)} TransitionComponent={Zoom}>
                <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {viewDiet && (
                        <Card sx={{ maxWidth: 700, width: '100%', borderRadius: 5, p: { xs: 2, md: 4 }, position: 'relative' }}>
                            <IconButton onClick={() => setViewDiet(null)} sx={{ position: 'absolute', top: 10, right: 10 }}><CloseIcon /></IconButton>
                            <Typography variant={isMobile ? 'h4' : 'h3'} fontWeight="bold" gutterBottom>{viewDiet.name}</Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>{viewDiet.description}</Typography>
                            <Button fullWidth size="large" variant="contained" sx={{ bgcolor: viewDiet.color }} onClick={(e) => handleBuyClick(viewDiet, e)}>
                                Купи сега за {viewDiet.price} ден
                            </Button>
                        </Card>
                    )}
                </Box>
            </Dialog>

            {/* STEPPER DIALOG */}
            <Dialog open={purchaseDialog.open} onClose={() => setPurchaseDialog({ open: false, diet: null })} maxWidth="sm" fullWidth>
                <DialogTitle>🛒 Купување: {purchaseDialog.diet?.name}</DialogTitle>
                <DialogContent>
                    <Stepper activeStep={activeStep} sx={{ py: 3 }}>
                        {STEPS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
                    </Stepper>
                    {activeStep === 0 && (
                        <Box sx={{ mt: 2 }}>
                            <TextField fullWidth label="Висина (cm)" margin="normal" value={bmiData.height} onChange={e => setBmiData({ ...bmiData, height: e.target.value })} />
                            <TextField fullWidth label="Тежина (kg)" margin="normal" value={bmiData.weight} onChange={e => setBmiData({ ...bmiData, weight: e.target.value })} />
                            <TextField fullWidth label="Обем на струк (cm)" margin="normal" value={bmiData.waistCircumference} onChange={e => setBmiData({ ...bmiData, waistCircumference: e.target.value })} />
                        </Box>
                    )}
                    {activeStep === 1 && (
                        <Box sx={{ mt: 2 }}>
                            <TextField fullWidth label="Возраст" margin="normal" value={questionnaire.age} onChange={e => setQuestionnaire({ ...questionnaire, age: e.target.value })} />

                            {/* ✅ НОВО - Цел на диетата */}
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Цел на диетата"
                                placeholder="на пр. слабеење, зголемување на мускулна маса, одржување..."
                                margin="normal"
                                value={questionnaire.dietGoal}
                                onChange={e => setQuestionnaire({ ...questionnaire, dietGoal: e.target.value })}
                            />

                            <TextField fullWidth multiline rows={3} label="Здравствени проблеми" margin="normal" value={questionnaire.healthProblems} onChange={e => setQuestionnaire({ ...questionnaire, healthProblems: e.target.value })} />
                            <TextField fullWidth multiline rows={2} label="Нетолеранција на храна" margin="normal" value={questionnaire.foodIntolerances} onChange={e => setQuestionnaire({ ...questionnaire, foodIntolerances: e.target.value })} />
                            <TextField fullWidth multiline rows={2} label="Суплементи" margin="normal" value={questionnaire.supplements} onChange={e => setQuestionnaire({ ...questionnaire, supplements: e.target.value })} />

                            {/* ✅ НОВО - Опционално прикачување слика од крвна анализа */}
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 'bold' }}>
                                    Слика од крвна анализа (опционално)
                                </Typography>

                                {!bloodTestPreview ? (
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        startIcon={<CloudUpload />}
                                        sx={{ borderRadius: 2, textTransform: 'none' }}
                                    >
                                        Прикачи слика
                                        <input
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={handleBloodTestChange}
                                        />
                                    </Button>
                                ) : (
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        p: 1.5,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2
                                    }}>
                                        <Box
                                            component="img"
                                            src={bloodTestPreview}
                                            alt="Преглед на крвна анализа"
                                            sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1 }}
                                        />
                                        <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
                                            {bloodTestFile?.name}
                                        </Typography>
                                        <IconButton onClick={handleRemoveBloodTest} size="small" color="error">
                                            <DeleteOutline />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                    {activeStep === 2 && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="h6" gutterBottom>Потврдете го купувањето</Typography>
                            <Typography color="text.secondary">Диета: <strong>{purchaseDialog.diet?.name}</strong></Typography>
                            <Typography color="text.secondary">Цена: <strong>{purchaseDialog.diet?.price} ден</strong></Typography>
                        </Box>
                    )}
                    {activeStep === 3 && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <CheckCircle sx={{ fontSize: 60, color: '#4CAF50', mb: 2 }} />
                                <Typography variant="h6">Прашалникот е испратен!</Typography>
                            </Box>

                            {/* ✅ НОВО - Инструкции за плаќање, исто како кај Програми */}
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body1" mb={2} sx={{ color: 'text.primary' }}>
                                    За да ја активирате диетата <strong>{purchaseDialog.diet?.name}</strong>, извршете уплата од <strong>{purchaseDialog.diet?.price} ден.</strong> на следната сметка:
                                </Typography>

                                <Box sx={{
                                    bgcolor: 'background.default',
                                    p: 2,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}>
                                    <Typography sx={{ color: 'text.primary' }}><strong>Примач:</strong> Anamarija Angelkoska</Typography>
                                    <Typography sx={{ color: 'text.primary' }}><strong>Сметка:</strong> 210501755493342</Typography>
                                    <Typography sx={{ color: 'text.primary' }}><strong>Банка:</strong> НЛБ Банка</Typography>
                                </Box>

                                <Typography variant="body2" mt={2} color="error" fontWeight="bold">
                                    ВАЖНО: Во "Цел на дознака" задолжително наведете:
                                </Typography>
                                <Typography variant="body2" sx={{
                                    bgcolor: isDarkMode ? 'rgba(244, 67, 54, 0.15)' : '#fff3f3',
                                    color: 'text.primary',
                                    p: 1,
                                    mt: 1,
                                    borderRadius: 1
                                }}>
                                    {purchaseDialog.diet?.name} - Вашиот Емаил
                                </Typography>

                                <Typography variant="body2" mt={2} sx={{ color: 'text.secondary' }}>
                                    Откако ќе ја извршите уплатата, админот ќе ја провери вашата трансакција и ќе ви овозможи пристап во најкраток можен рок.
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {activeStep < 3 && (
                        <>
                            <Button onClick={() => setPurchaseDialog({ open: false, diet: null })}>Откажи</Button>
                            <Button
                                variant="contained"
                                disabled={submitting}
                                onClick={activeStep === 2 ? handleFinalSubmit : () => setActiveStep(s => s + 1)}
                            >
                                {activeStep === 2 ? (submitting ? 'Се обработува...' : 'Потврди') : 'Следно'}
                            </Button>
                        </>
                    )}
                    {activeStep === 3 && (
                        <Button variant="contained" onClick={() => setPurchaseDialog({ open: false, diet: null })}>Затвори</Button>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ borderRadius: 3, fontWeight: 'bold', fontSize: '0.95rem' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Footer />
        </>
    );
};

export default UserBuyDiets;